import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { DateRange } from '@/components/DateRangeSelector'
import dayjs from 'dayjs'
import { TrendingUp, TrendingDown } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { Skeleton } from '@/components/ui/skeleton'
import { getWorkdaysReport } from '../../../services'

interface OnTimeRateWidgetProps {
    dateRange: DateRange
}

const OnTimeRateWidget: React.FC<OnTimeRateWidgetProps> = ({ dateRange }) => {
    const { t } = useTranslation()

    // Calculate previous month range
    const prevDateRange = useMemo(() => {
        const currentStart = dayjs(dateRange.gte)
        const prevStart = currentStart.subtract(1, 'month').startOf('month')
        const prevEnd = currentStart.subtract(1, 'month').endOf('month')
        return {
            gte: prevStart.format('YYYY-MM-DD'),
            lte: prevEnd.format('YYYY-MM-DD'),
        }
    }, [dateRange])

    // Fetch current month data
    const { data: currentData, isLoading: isLoadingCurrent } =
        useQueryWithHandler(
            {
                queryKey: [
                    QUERY_KEYS.WORKDAYS_REPORT,
                    'ontime-current',
                    dateRange.gte,
                    dateRange.lte,
                ],
                queryFn: () => getWorkdaysReport(dateRange),
                staleTime: 5 * 60 * 1000,
            },
            { showLoading: false, showError: false }
        )

    // Fetch previous month data
    const { data: prevData, isLoading: isLoadingPrev } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.WORKDAYS_REPORT,
                'ontime-prev',
                prevDateRange.gte,
                prevDateRange.lte,
            ],
            queryFn: () => getWorkdaysReport(prevDateRange),
            staleTime: 5 * 60 * 1000,
        },
        { showLoading: false, showError: false }
    )

    // Calculate on-time rate
    const { currentRate, diff } = useMemo(() => {
        const calculateRate = (data: any[] | undefined) => {
            if (!data || data.length === 0) return 0
            const totalAttendance = data.reduce(
                (sum, emp) => sum + (emp.attendance_days || 0),
                0
            )
            const totalLate = data.reduce(
                (sum, emp) => sum + (emp.late_days || 0),
                0
            )
            if (totalAttendance === 0) return 0
            return ((totalAttendance - totalLate) / totalAttendance) * 100
        }

        const current = calculateRate(currentData)
        const prev = calculateRate(prevData)
        const difference = current - prev

        return {
            currentRate: current,
            diff: difference,
        }
    }, [currentData, prevData])

    const isLoading = isLoadingCurrent || isLoadingPrev
    const isPositive = diff >= 0

    if (isLoading) {
        return (
            <div className="bg-white rounded-lg p-5 shadow-md border border-gray-100 h-full flex flex-col relative overflow-hidden">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500" />
                <Skeleton className="h-4 w-32 mb-2" />
                <Skeleton className="h-12 w-20 mb-2" />
                <div className="flex-1" />
                <Skeleton className="h-4 w-24" />
            </div>
        )
    }

    return (
        <div
            className="bg-white rounded-lg p-5 shadow-md border border-gray-100 h-full flex flex-col relative overflow-hidden"
            data-tour="workday-ontime-rate"
        >
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500" />
            <div className="flex items-start justify-between">
                <div>
                    <Typogaphy className="text-base font-medium text-primary italic">
                        {t(
                            'workday.ontime_rate_title',
                            'Tỷ lệ đi làm đúng giờ'
                        )}
                    </Typogaphy>
                    <Typogaphy className="text-base text-primary italic">
                        {t('workday.company_wide', 'của toàn công ty')}
                    </Typogaphy>
                </div>
                <div className="text-right">
                    <Typogaphy className="text-4xl font-bold text-primary">
                        {currentRate.toFixed(0)}%
                    </Typogaphy>
                </div>
            </div>

            {/* Spacer to push comparison to bottom */}
            <div className="flex-1" />

            {/* Comparison with previous month */}
            <div className="pt-3">
                <div
                    className={`flex items-center gap-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}
                >
                    {isPositive ? (
                        <TrendingUp className="w-4 h-4" />
                    ) : (
                        <TrendingDown className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium">
                        {isPositive ? '+' : ''}
                        {diff.toFixed(1)}%
                    </span>
                    <span className="text-sm text-gray-500">
                        {t('workday.vs_prev_month', 'So với tháng trước')}
                    </span>
                </div>
            </div>
        </div>
    )
}

export default OnTimeRateWidget
